import DeleteIcon from '@mui/icons-material/Delete';
import {
    TextField, MenuItem, Typography, Box, Button, Paper,
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
    IconButton, Snackbar, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tabs, Tab, Checkbox, FormControlLabel,
    List, ListItem, ListItemButton, ListItemText, LinearProgress,
    FormControl, Select, InputLabel
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import { useTheme } from '@mui/material/styles';
import MuiAlert from '@mui/material/Alert';
import React from 'react';
// Assuming EmailIcon is not strictly needed here, but kept in the list for completeness.

// --- Helper Functions for Form Components ---
const FormField = ({ labelEng, labelAfr, name, type = "text", value, onChange, isPreview, isMandatory = true, multiline = false, rows = 1, select = false, ...rest }) => {
    const inputProps = isPreview ? { readOnly: true } : {};
    const labelContent = (
        <>
            {isMandatory ? '*' : ''} {labelEng} / <span style={{ fontWeight: 'normal' }}>{labelAfr}</span>{isMandatory ? '*' : ''}
        </>
    );
    return (
        <TextField
            label={labelContent}
            fullWidth
            margin="normal"
            name={name}
            type={type}
            size="small"
            value={value}
            onChange={onChange}
            InputLabelProps={{ shrink: true }}
            InputProps={inputProps}
            disabled={isPreview}
            multiline={multiline}
            rows={rows}
            select={select}
            {...rest}
        >
            {/* children needed for select */}
            {rest.children}
        </TextField>
    );
};
// --- End Helper Functions ---

// 🛠️ NEW HELPER: Currency Adornment for the Fee field
const CurrencyAdornment = () => (
    <Typography variant="body1" sx={{ mr: 1, color: 'text.secondary' }}>R</Typography>
);


// Custom TabPanel component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`accounts-tabpanel-${index}`}
            aria-labelledby={`accounts-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// --- Placeholder/Mock Form Components (Kept for reference, but removed from render) ---
// const Form04Statutory = ...
// const Form05Statutory = ...
// const Form07Statutory = ...
// --------------------------------------------------------------------------------------


const AccountsTab = ({ matter, estateId }) => {
    const [tableData, setTableData] = useState([]);
    const [estateNos, setEstateNos] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isInvitedUser, setIsInvitedUser] = useState(false);
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({
        accountType: "",
        dueDate: "",
        followUpDate: "",
        extensionDate: "",
        form4Date: "",
        lodgedDate: "",
        confirmedDate: "",
        releaseDate: "",
        receiptFollowUp: "",
        fee: "", // This is the fee field we need to update
        comments: "",
        legalMatterId: "",
        estateId: "",
        draftedBy: "", // Added draftedBy to form data
    });
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const theme = useTheme();
    const [estate, setEstate] = useState(null);

    // NEW Tab State
    const [selectedTab, setSelectedTab] = useState(0);

    // Extract 'legalMatterId' and 'estateId' from props
    const legalMatterId = matter?.id || '';
    const estateIdFromMatter = matter?.EstateID || estateId || '';

    // --- Handlers ---
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const formatDate = (date) => {
        if (!date) return '';
        const formattedDate = new Date(date);
        return formattedDate.toISOString().split('T')[0];
    };

    const remapOldAccounts = (oldAccounts) => {
        if (!Array.isArray(oldAccounts)) {
            console.error('Expected oldAccounts to be an array, but got:', oldAccounts);
            return [];
        }

        return oldAccounts
            .filter(account => account.Deleted !== 1)
            .map(account => ({
                id: account.ID,
                estateId: account.EstateID,
                accountType: account.AccountType,
                dueDate: formatDate(account.DueDate),
                extensionDate: formatDate(account.ExtensionDate),
                lodgedDate: formatDate(account.LodgedDate),
                followUpDate: formatDate(account.FollowUpDate),
                form4Date: formatDate(account.Form4Date),
                confirmedDate: formatDate(account.ConfirmedDate),
                releaseDate: formatDate(account.ReleaseDate),
                receiptsDate: formatDate(account.ReceiptsDate),
                recDateFollow: formatDate(account.RecDateFollow),
                comments: [account.Remarks, account.Remarks2].filter(Boolean).join(' ').trim(),
                fees: parseFloat(account.Fees) || 0,
                compiler: account.Compiler,
                draftedBy: account.Compiler, // Map Compiler to draftedBy for consistency
            }));
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - (offset * 60 * 1000));
        return localDate.toISOString().split('T')[0]; // yyyy-MM-dd
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await axios.get('https://ipms.insollex.com/api/user', { withCredentials: true });
                if (typeof res.data !== 'object' || res.data === null) {
                    setUser(null);
                    return;
                }
                if (!res.data.role) {
                    res.data.role = "guest";
                }
                setUser(res.data);
            } catch (err) {
                setUser(null);
            }
        };

        fetchUser();
    }, []);

    const fetchAccounts = async () => {
        try {
            let request = null;

            if (legalMatterId) {
                request = axios.get(`https://ipms.insollex.com/api/accounts?legalMatterId=${legalMatterId}`);
            }
            else if (estateIdFromMatter && estateIdFromMatter !== "0") {
                // request = axios.get(`https://ipms.insollex.com/api/old-database/accounts/${estateIdFromMatter}`);
            }

            if (request) {
                const response = await request;
                const accountsArray = response.data?.data ?? response.data ?? [];

                const formattedData = (legalMatterId)
                    ? accountsArray
                    : remapOldAccounts(accountsArray);

                if (Array.isArray(formattedData) && formattedData.length > 0) {
                    setTableData(formattedData);
                } else {
                    setSnackbarMessage("No accounts found for the specified identifier.");
                    setSnackbarSeverity("warning");
                    setOpenSnackbar(true);
                }
            } else {
                setSnackbarMessage("Please provide a valid Legal Matter ID or Estate ID.");
                setSnackbarSeverity("warning");
                setOpenSnackbar(true);
            }
        } catch (error) {
            setSnackbarMessage("Error fetching accounts.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    useEffect(() => {
        if (legalMatterId || estateIdFromMatter) {
            fetchAccounts();
        }
    }, [legalMatterId, estateIdFromMatter]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleAdd = async () => {
        if (!legalMatterId || !formData.accountType || !formData.dueDate) {
            setSnackbarMessage("Please fill in all required fields.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            return;
        }

        const newAccount = {
            ...formData,
            legalMatterId: legalMatterId,
        };

        try {
            const response = await axios.post('https://ipms.insollex.com/api/accounts', newAccount);
            setTableData([...tableData, response.data]);
            setSnackbarMessage("Account added successfully.");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
            resetForm();
        } catch (error) {
            setSnackbarMessage("Error adding account.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };


    const handleEdit = (row) => {
        setSelectedRow(row);
        setFormData({
            ...row,
            // Use 'fees' from old DB mapped data, or 'fee' from new DB
            fee: row.fee !== undefined ? row.fee : row.fees || "",
            legalMatterId: row.legalMatterId || estateNos[0]?.id || "",
            estateId: row.estateId || estateNos[0]?.id || "",
            draftedBy: row.draftedBy || "",
        });
    };

    const handleSave = async () => {
        if (!selectedRow) return;

        try {
            const updatedAccount = {
                ...formData,
                estateId: estateIdFromMatter,
            };

            await axios.put(`https://ipms.insollex.com/api/accounts/${selectedRow.id}`, updatedAccount);
            fetchAccounts(); // Refresh data to ensure all new data structures are picked up
            setSnackbarMessage('Account updated successfully.');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            resetForm();
        } catch (error) {
            setSnackbarMessage('Error updating account.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleDelete = async () => {
        if (selectedRow) {
            try {
                await axios.delete(`https://ipms.insollex.com/api/accounts/${selectedRow.id}`);
                setTableData(tableData.filter((row) => row.id !== selectedRow.id));
                resetForm();
                setSnackbarMessage('Account deleted successfully.');
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
            } catch (error) {
                setSnackbarMessage('Error deleting account.');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            accountType: "",
            dueDate: "",
            followUpDate: "",
            extensionDate: "",
            form4Date: "",
            lodgedDate: "",
            confirmedDate: "",
            releaseDate: "",
            receiptFollowUp: "",
            fee: "",
            comments: "",
            legalMatterId: estateNos[0]?.id || "",
            estateId: estateNos[0]?.id || "",
            draftedBy: "",
        });
        setSelectedRow(null);
    };

    const handleCancel = () => {
        resetForm();
    }

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    const accountTypeOptions = [
        { label: 'FIRST LIQUIDATION AND DISTRIBUTION' },
        { label: 'FIRST LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
        { label: 'AMENDED FIRST LIQUIDATION & DISTRIBUTION' },
        { label: 'AMENDED FIRST LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'FIRST & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'FIRST & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
        { label: 'AMENDED FIRST & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'AMENDED FIRST & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'SUPPLEMENTARY FIRST & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'SUPPLEMENTARY FIRST & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'FIRST & FINAL LIQUIDATION' },
        { label: 'FIRST & FINAL LIQUIDATION & CONTRIBUTION' },
        { label: 'AMENDED FIRST & FINAL LIQUIDATION' },
        { label: 'AMENDED FIRST & FINAL LIQUIDATION & CONTRIBUTION' },

        { label: 'SECOND LIQUIDATION AND DISTRIBUTION' },
        { label: 'SECOND LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
        { label: 'AMENDED SECOND LIQUIDATION & DISTRIBUTION' },
        { label: 'AMENDED SECOND LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'SECOND & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'SECOND & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
        { label: 'AMENDED SECOND & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'AMENDED SECOND & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'SUPPLEMENTARY SECOND & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'SUPPLEMENTARY SECOND & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'THIRD LIQUIDATION AND DISTRIBUTION' },
        { label: 'THIRD LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
        { label: 'AMENDED THIRD LIQUIDATION & DISTRIBUTION' },
        { label: 'AMENDED THIRD LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'THIRD & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'THIRD & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
        { label: 'AMENDED THIRD & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'AMENDED THIRD & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },

        { label: 'SUPPLEMENTARY THIRD & FINAL LIQUIDATION & DISTRIBUTION' },
        { label: 'SUPPLEMENTARY THIRD & FINAL LIQUIDATION, DISTRIBUTION & CONTRIBUTION' },
    ];


    // --- Component Render ---
    return (
        <Grid container spacing={2}>
            {/* Tab Header */}
            <Grid item xs={12}>
                {/* Centering the Tabs component */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                    <Tabs 
                        value={selectedTab} 
                        onChange={handleTabChange} 
                        aria-label="Account Forms Tabs" 
                        variant="scrollable" 
                        scrollButtons="auto"
                    >
                        <Tab label="Accounts" />
                        <Tab label="Form 4" />
                        <Tab label="Form 5" />
                        <Tab label="Form 7" />
                    </Tabs>
                </Box>
            </Grid>

            {/* Tab Panel 0: Accounts Table & Entry Form (Existing Content) */}
            <TabPanel value={selectedTab} index={0}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" align="center" sx={{ color: 'text.primary' }}>Accounts</Typography>
                    </Grid>

                    {/* Account Fields Section */}
                    <Grid item xs={12}>
                        <Paper sx={{ padding: 3, border: '1px solid #ccc' }}>
                            <Grid container spacing={2}>
                                {/* Drafted By */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Drafted By"
                                        name="draftedBy"
                                        value={formData.draftedBy || ""}
                                        variant="outlined"
                                        onChange={handleChange}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" disabled={isInvitedUser}>
                                        <InputLabel>Account Type</InputLabel>
                                        <Select
                                            name="accountType"
                                            value={formData.accountType || ""}
                                            onChange={handleChange}
                                            label="Account Type"
                                            disabled={user?.role === 'guest' || user?.isInvited}
                                        >
                                            {accountTypeOptions.map(({ label }) => (
                                                <MenuItem key={label} value={label}>
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Due Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Due Date"
                                        name="dueDate"
                                        type="date"
                                        value={formatDateForInput(formData.dueDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Follow Up Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Follow Up Date"
                                        name="followUpDate"
                                        type="date"
                                        value={formatDateForInput(formData.followUpDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Extension Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Extension Date"
                                        name="extensionDate"
                                        type="date"
                                        value={formatDateForInput(formData.extensionDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Form 4 Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Form 4 Date"
                                        name="form4Date"
                                        type="date"
                                        value={formatDateForInput(formData.form4Date)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Lodged Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Lodged Date"
                                        name="lodgedDate"
                                        type="date"
                                        value={formatDateForInput(formData.lodgedDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Confirmed Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Confirmed Date"
                                        name="confirmedDate"
                                        type="date"
                                        value={formatDateForInput(formData.confirmedDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Release Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Release Date"
                                        name="releaseDate"
                                        type="date"
                                        value={formatDateForInput(formData.releaseDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>

                                {/* Fee */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fee"
                                        name="fee"
                                        type="number"
                                        value={formData.fee || ""}
                                        variant="outlined"
                                        onChange={handleChange}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                        // 🛠️ ADDED: Currency Adornment for Fee Input
                                        InputProps={{
                                            startAdornment: <CurrencyAdornment />,
                                        }}
                                    />
                                </Grid>

                                {/* Comments */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Comments"
                                        name="comments"
                                        value={formData.comments || ""}
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        onChange={handleChange}
                                        disabled={user?.role === 'guest' || user?.isInvited}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Buttons Section */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-start" gap={1}>
                            <Button variant="outlined" onClick={handleAdd} disabled={user?.role === 'guest' || user?.isInvited}>Save</Button>
                            <Button variant="outlined" onClick={handleSave} disabled={!selectedRow || user?.role === 'guest' || user?.isInvited}>Save</Button>
                            <Button variant="outlined" onClick={handleCancel} disabled={user?.role === 'guest' || user?.isInvited}>Cancel</Button>
                            {selectedRow && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={handleDelete}
                                    disabled={user?.role === 'guest' || user?.isInvited}
                                    startIcon={<DeleteIcon />}
                                >
                                </Button>
                            )}
                        </Box>
                    </Grid>

                    {/* Table Section */}
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                padding: 2,
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                bgcolor: theme.palette.background.paper,
                            }}
                        >
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {[
                                                'Drafted By',
                                                'Account Type',
                                                'Due Date',
                                                'Lodged Date',
                                                'Extension Date',
                                                'Form 4 Date',
                                                'Confirmed Date',
                                                'Release Date',
                                                'Follow Up Date',
                                                'Fee', // The Fee header
                                            ].map((header) => (
                                                <TableCell key={header} sx={{ color: theme.palette.text.primary }}>
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ color: theme.palette.text.secondary }}>
                                                    No accounts available.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            tableData.map((row, index) => (
                                                <TableRow
                                                    key={index}
                                                    onClick={() => handleEdit(row)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        bgcolor:
                                                            selectedRow === row ? theme.palette.action.selected : 'inherit',
                                                        '&:hover': {
                                                            bgcolor: theme.palette.action.hover,
                                                        },
                                                    }}
                                                >
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{row.draftedBy || row.compiler}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{row.accountType || ''}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.dueDate)}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.lodgedDate)}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.extensionDate)}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.form4Date)}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.confirmedDate)}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.releaseDate)}</TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>{formatDateForInput(row.followUpDate)}</TableCell>

                                                    {/* 🛠️ ADDED: 'R' prefix and formatting for Fee in Table */}
                                                    <TableCell>
                                                        <Typography
                                                            sx={{
                                                                color:
                                                                    !isNaN(Number((row.fee || row.fees || '').toString().replace(/,/g, ''))) &&
                                                                        Number((row.fee || row.fees || '').toString().replace(/,/g, '')) < 0
                                                                        ? 'red'
                                                                        : theme.palette.text.primary,
                                                                fontWeight:
                                                                    Number((row.fee || row.fees || '').toString().replace(/,/g, '')) < 0 ? 'bold' : 'normal',
                                                            }}
                                                        >
                                                            R {
                                                                !isNaN(Number((row.fee || row.fees || '').toString().replace(/,/g, '')))
                                                                    ? parseFloat((row.fee || row.fees).toString().replace(/,/g, '')).toFixed(2)
                                                                    : '0.00'
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </TabPanel>

            {/* ❌ MODIFIED: Tab Panel 1: Form 4 Content (Under Construction) */}
            <TabPanel value={selectedTab} index={1}>
                <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary" gutterBottom>
                        Statutory Form 4 is Under Construction 🚧
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The functionality for this statutory form will be available soon.
                    </Typography>
                </Box>
            </TabPanel>

            {/* ❌ MODIFIED: Tab Panel 2: Form 5 Content (Under Construction) */}
            <TabPanel value={selectedTab} index={2}>
                <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary" gutterBottom>
                        Statutory Form 5 is Under Construction 🚧
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The functionality for this statutory form will be available soon.
                    </Typography>
                </Box>
            </TabPanel>

            {/* ❌ MODIFIED: Tab Panel 3: Form 7 Content (Under Construction) */}
            <TabPanel value={selectedTab} index={3}>
                <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary" gutterBottom>
                        Statutory Form 7 is Under Construction 🚧
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The functionality for this statutory form will be available soon.
                    </Typography>
                </Box>
            </TabPanel>

            {/* Snackbar */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
        </Grid>
    );
};

export default AccountsTab;